export enum STATUS_CODE {
  SEE_ORDER_DETAILS = 100,
  PROCESSING = 101,
  SHIPPED = 102,
  IN_TRANSIT = 103,
  ATTEMPTED = 104,
  DELIVERED = 105,
  BACKORDERED = 106,
  WORK_ORDER_CANCELED = 107,
  MAJOR_APPLIANCE_ON_SCHEDULE = 108,
  OUT_FOR_DELIVERY = 109,
  DELIVERY_ATTEMPTED = 110,
  BODFS_ON_SCHEDULE = 111,
  ON_ITS_WAY_TO_STORE = 130,
  READY_FOR_PICKUP = 131,
  PREPARING_FOR_PICKUP = 132,
  CANCELED = 140,
  RETURNED = 150,
  RETURN_IN_PROGRESS = 151,
  COMPLETED = 200,
  DFS_DELIVERED = 1000,
  DFS_ON_SCHEDULE = 401,
}

export enum FULFILLMENT_TYPE {
  STH = 'ShipToHome',
  BOSS = 'BuyOnlineShipToStore',
  MA = 'Appliance',
  BOPIS = 'BuyOnlinePickupInStore',
  BODFS = 'BuyOnlineDeliverFromStore',
  DFS = 'DeliverFromStore',
  APPLIANCE = MA, // Had to add this in order to "type" OrderStatus
  MISC = 'MISC',
  CANCEL = 'CancelledItems',
  ADDITIONAL_ITEMS = 'Additional Items',
  ISP = 'InStorePurchase', // This enum member might not be accurate, since there's no acronym for InStorePurchase, but we needed this value
}

export type FulfillmentTypes = keyof typeof FULFILLMENT_TYPE;

/**
 * Compact analytics labels for FULFILLMENT_TYPE ('ShipToHome' -> 'STH'), so New Relic
 * facets read as acronyms. Written out rather than inverted from Object.entries, since
 * APPLIANCE and MA share the value 'Appliance'. Insertion order is load-bearing: it is
 * the canonical order for '+'-joined keys ('STH+BOSS', never 'BOSS+STH') — do not
 * alphabetize. Typed exhaustively so a new member is a compile error, not a silent UNKNOWN.
 */
export const FULFILLMENT_TYPE_CODE: Record<FULFILLMENT_TYPE, string> = {
  [FULFILLMENT_TYPE.STH]: 'STH',
  [FULFILLMENT_TYPE.BOSS]: 'BOSS',
  [FULFILLMENT_TYPE.BOPIS]: 'BOPIS',
  [FULFILLMENT_TYPE.BODFS]: 'BODFS',
  [FULFILLMENT_TYPE.DFS]: 'DFS',
  [FULFILLMENT_TYPE.MA]: 'MA',
  [FULFILLMENT_TYPE.ISP]: 'ISP',
  [FULFILLMENT_TYPE.MISC]: 'MISC',
  [FULFILLMENT_TYPE.CANCEL]: 'CANCEL',
  [FULFILLMENT_TYPE.ADDITIONAL_ITEMS]: 'ADDITIONAL_ITEMS',
};

/** Label for a selected item whose lineItemId has no deliveryDictionary entry. */
export const FULFILLMENT_CODE_UNKNOWN = 'UNKNOWN';

type OrderStatus = {
  [FKey in FulfillmentTypes]?: {
    [SKey in STATUS_CODE]?: string;
  };
};

export const ORDER_STATUS: OrderStatus = {
  STH: {
    101: 'Processing',
    102: 'On its way',
    105: 'Delivered',
    140: '',
  },
  BOSS: {
    101: 'Processing',
    102: '',
    105: 'Your order is ready',
    130: 'On its way to the store',
    131: 'Your order is ready',
    132: 'The store is preparing your order',
    200: 'Picked up',
    140: '',
  },
  BOPIS: {
    101: 'Processing',
    102: '',
    105: 'Your order is ready',
    130: '',
    131: 'Your order is ready',
    200: 'Picked up',
    140: '',
  },
  APPLIANCE: {
    101: 'On schedule',
    102: 'Out for delivery',
    105: 'Delivered',
    108: 'On schedule',
    109: 'Out for delivery',
    110: 'Delivery attempted',
    200: 'Order Completed',
    140: '',
  },
  BODFS: {
    101: 'Processing',
    105: 'Delivered',
    109: 'Out for delivery',
    111: 'On Schedule',
    140: '',
  },
  DFS: {
    101: 'Processing',
    105: 'Delivered',
    109: 'Out for delivery',
    111: 'On Schedule',
    401: 'Processing',
    1000: 'Delivered',
    140: '',
  },
  MISC: {
    101: '',
    102: '',
    105: 'Delivered',
    108: '',
    109: '',
    110: '',
    200: 'Order Completed',
    140: '',
  },
  // Just a placeholder to fix the Lint: this is not required
  CANCEL: {
    101: '',
    102: '',
    105: '',
    108: '',
    109: '',
    110: '',
    200: '',
    140: '',
  },
  ADDITIONAL_ITEMS: {
    101: '',
    102: '',
    105: '',
    108: '',
    109: '',
    110: '',
    200: '',
    140: '',
  },
};

type OrderStatusMessage = {
  [FKey in FULFILLMENT_TYPE | 'BACKORDERED']: FKey extends FULFILLMENT_TYPE
    ? { [SKey in STATUS_CODE]?: string }
    : string;
};

export const ORDER_STATUS_MESSAGE: OrderStatusMessage = {
  InStorePurchase: {},
  ShipToHome: {
    101: 'Arriving ',
    102: 'Arriving ',
    103: 'Arriving ',
    104: 'Arriving ',
    105: 'Arrived ',
    150: 'We received your return ',
    151: 'Drop off your package at UPS ',
    140: '',
  },
  BuyOnlineShipToStore: {
    101: 'Ready for Pick up ',
    102: 'Ready for Pick up ',
    103: 'Ready for Pick up ',
    104: 'Ready for Pick up ',
    105: 'Ready for Pick up ',
    130: 'Ready for Pick up ',
    131: 'Ready for Pick up ',
    132: 'Ready for Pick up ',
    150: 'We received your return ',
    151: 'Drop off your package at UPS ',
    200: 'Picked up ',
    140: '',
  },
  BuyOnlinePickupInStore: {
    101: 'Ready by ',
    102: 'Ready by ',
    103: 'Ready by ',
    104: 'Ready by ',
    105: 'Ready by ',
    131: 'Ready by ',
    150: 'We received your return ',
    151: 'Drop off your package at UPS ',
    200: 'Picked up ',
    140: '',
  },
  Appliance: {
    101: 'Delivery Scheduled for ',
    102: 'Delivery Window: ',
    103: 'Delivery Attemped on ',
    104: '',
    105: 'Arrived on ',
    108: 'Delivery Scheduled for ',
    109: 'Delivery Window: ',
    110: 'Delivery Attempted on ',
    150: 'We received your return ',
    151: 'Drop off your package at UPS ',
    200: 'Your Order has Been Completed',
    140: '',
  },
  MISC: {
    101: 'Arriving ',
    102: 'Arriving ',
    103: 'Arriving ',
    104: 'Arriving ',
    105: 'Arrived ',
    150: 'We received your return ',
    151: 'Drop off your package at UPS ',
    140: '',
  },
  BuyOnlineDeliverFromStore: {
    101: 'Delivery Window: ',
    105: 'Delivered ',
    109: 'Delivery Window: ',
    111: 'Delivery Window: ',
    150: 'We received your return ',
    151: 'Drop off your package at UPS ',
    140: '',
  },
  DeliverFromStore: {
    101: 'Delivery Window: ',
    105: 'Delivered ',
    109: 'Delivery Window: ',
    111: 'Delivery Window: ',
    150: 'We received your return ',
    151: 'Drop off your package at UPS ',
    401: 'Delivery Window: ',
    1000: 'Delivered ',
    140: '',
  },
  // just a placeholder
  CancelledItems: {
    101: '',
    105: '',
    109: '',
    111: '',
    150: ' ',
    151: ' ',
    401: '',
    1000: ' ',
    140: '',
  },

  'Additional Items': {
    101: '',
    102: '',
    105: '',
    108: '',
    109: '',
    110: '',
    200: '',
    140: '',
  },

  BACKORDERED: `Unfortunately, these items are currently out of stock. Once shipped,
  we'll email you the tracking details. Your payment won't be processed
  until then.`,
};
